import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Rides } from '../..';
import { renderColumns } from './renderColumns';
import { renderTableHead } from './renderTableHead';
import { fetchAllTransportOrganizations } from '../../../store/transport_organizations';
import { fetchAllHealthOrganizations } from '../../../store/health_organizations';
import { setMyFilter, setFilteredStatus, getCustomUserFilter, saveCustomUserFilter } from '../../../store/rides';
import { RIDE_STATUSES } from '../../../constants/constants';

import styles from './index.module.css';

const MH_RIDES_COLUMN_WIDTHS = [180, 180, 155, 150, 250, 200, 220, 120];
const MH_RIDES_TABLE_WIDTH = MH_RIDES_COLUMN_WIDTHS.reduce((total, width) => total + width, 0);

const syncMhRidesTableColumns = (container) => {
  if (!container) return;

  container.querySelectorAll('.ant-table colgroup col').forEach((col, index) => {
    const width = MH_RIDES_COLUMN_WIDTHS[index];
    if (width) {
      col.style.width = `${width}px`;
    }
  });

  const header = container.querySelector('.ant-table-header');
  const body = container.querySelector('.ant-table-body');
  if (header && body) {
    header.scrollLeft = body.scrollLeft;
  }
};

export const transofrmOrganizationsOptions = (organizations) =>
  organizations.map((h) => ({
    key: h.id,
    title: h.name,
    value: { id: h.id, name: h.name },
    ...(h.children && {
      children: h.children.map((child) => ({
        key: child.id,
        title: child.name,
        value: { id: child.id, name: child.name },
      })),
    }),
  }));

/** Health org filter: Enterprise vs Standard sections (Figma-aligned). */
export const transformHealthOrganizationsGroupedFilter = (organizations) => {
  const enterprise = [];
  const standard = [];
  const organizationsList = organizations || [];

  const mappedChildrenByParentId = organizationsList.reduce((acc, organization) => {
    if (!organization?.parentOrgId) {
      return acc;
    }

    const childItem = {
      key: organization.id,
      title: organization.name,
      value: { id: organization.id, name: organization.name },
    };

    acc[organization.parentOrgId] = [...(acc[organization.parentOrgId] || []), childItem];
    return acc;
  }, {});

  organizationsList.forEach((h) => {
    if (!h || h.parentOrgId) return;

    const nestedChildren = h.children?.length
      ? h.children.map((child) => ({
          key: child.id,
          title: child.name,
          value: { id: child.id, name: child.name },
        }))
      : mappedChildrenByParentId[h.id] || [];

    const item = {
      key: h.id,
      title: h.name,
      value: { id: h.id, name: h.name },
      ...(nestedChildren.length && {
        children: nestedChildren,
      }),
    };

    const isEnterprise = h.isEnterprise === true || nestedChildren.length > 0;

    if (isEnterprise) enterprise.push(item);
    else standard.push(item);
  });

  return [
    { key: 'enterprise', title: 'Enterprise', children: enterprise, showNestedArrow: true },
    { key: 'standard', title: 'Standard', children: standard, showNestedArrow: false },
  ];
};

const MH_Rides = ({
  fetchAllTransportOrganizations,
  getCustomUserFilter,
  savedFilterParams,
  fetchAllHealthOrganizations,
  hasSavedFilterParams,
  transportOrganizations,
  healthOrganizations,
  setMyFilter,
  setFilteredStatus,
  isUberEligibleFromAuth,
  role,
  userRole,
}) => {
  const hasFetchedOrganizationsRef = useRef(false);
  const hasFetchedFilterRef = useRef(false);
  const tableContainerRef = useRef(null);
  const isUberEligible = Boolean(isUberEligibleFromAuth);
  const setRequestsOnly = useCallback(() => {
    setMyFilter({ status: [RIDE_STATUSES.IN_PROGRESS] });
    setFilteredStatus(true);
  }, [setMyFilter, setFilteredStatus]);

  useEffect(() => {
    return () => setFilteredStatus(false);
  }, [setFilteredStatus]);

  useEffect(() => {
    if (hasFetchedOrganizationsRef.current) return;
    hasFetchedOrganizationsRef.current = true;
    fetchAllTransportOrganizations();
    fetchAllHealthOrganizations();
    
    return () => { };
  }, [fetchAllTransportOrganizations, fetchAllHealthOrganizations]);

  useEffect(() =>{ 
    if (hasFetchedFilterRef.current) return;
    hasFetchedFilterRef.current = true;
    getCustomUserFilter();

    return () => {};
  }, [getCustomUserFilter]);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return undefined;

    let rafId = 0;
    let bodyEl = null;

    const onBodyScroll = () => {
      const header = container.querySelector('.ant-table-header');
      if (header && bodyEl) {
        header.scrollLeft = bodyEl.scrollLeft;
      }
    };

    const attachBodyScroll = () => {
      const nextBody = container.querySelector('.ant-table-body');
      if (!nextBody || nextBody === bodyEl) return;
      bodyEl?.removeEventListener('scroll', onBodyScroll);
      bodyEl = nextBody;
      bodyEl.addEventListener('scroll', onBodyScroll);
    };

    const scheduleSync = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        syncMhRidesTableColumns(container);
        attachBodyScroll();
      });
    };

    scheduleSync();
    window.addEventListener('resize', scheduleSync);
    window.visualViewport?.addEventListener('resize', scheduleSync);
    window.visualViewport?.addEventListener('scroll', scheduleSync);

    const resizeObserver = new ResizeObserver(scheduleSync);
    resizeObserver.observe(container);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', scheduleSync);
      window.visualViewport?.removeEventListener('resize', scheduleSync);
      window.visualViewport?.removeEventListener('scroll', scheduleSync);
      resizeObserver.disconnect();
      bodyEl?.removeEventListener('scroll', onBodyScroll);
    };
  }, [transportOrganizations, healthOrganizations, savedFilterParams]);

  const renderTableHeadConfig = useMemo(
    () =>
      renderTableHead({
        transportOrganizations: transofrmOrganizationsOptions(transportOrganizations),
        healthOrganizations: transformHealthOrganizationsGroupedFilter(healthOrganizations),
        isUberEligible,
      }),
    [transportOrganizations, healthOrganizations, isUberEligible],
  );

  return (
    <div className={styles.table} ref={tableContainerRef}>
      <Rides
        setRequestsOnly={setRequestsOnly}
        savedFilterParams={savedFilterParams}
        hasSavedFilterParams={hasSavedFilterParams}
        columns={renderColumns()}
        renderTableHead={renderTableHeadConfig}
        horizontalScroll={MH_RIDES_TABLE_WIDTH}
      />
    </div>
  );
};

const mapStateToProps = (state) => ({
  healthOrganizations: state.healthOrganizations.healthOrganizations,
  transportOrganizations: state.transportOrganizations.transportOrganizations,
  savedFilterParams: state.rides?.savedFilterParams,
  hasSavedFilterParams: state.rides?.savedFilterParams && Boolean(Object.keys(state.rides?.savedFilterParams)?.length),
  isUberEligibleFromAuth: state.authorization?.isUberEligible,
  role: state.authorization?.role,
  userRole: state.authorization?.user?.role,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchAllTransportOrganizations,
      setMyFilter,
      fetchAllHealthOrganizations,
      setFilteredStatus,
      getCustomUserFilter,
      saveCustomUserFilter,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(MH_Rides);
